import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import axios from '../../services/api';
import GoogleLoginButton from '../GoogleLoginButton';
import './Login.scss';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [currentView, setCurrentView] = useState('initial'); // 'initial', 'email', 'code'
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const { loginWithCode } = useAuth();
  const { t } = useI18n();

  if (!isOpen) return null;

  // Google登录成功处理函数
  const handleGoogleLoginSuccess = async (result) => {
    try {
      const { token, user } = result;
      
      // 保存 token 和用户信息
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // 设置 axios 默认 header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log('Google login successful, user:', user.email);
      
      // 调用成功回调并关闭模态框
      if (onLoginSuccess) {
        onLoginSuccess(result);
      }
      onClose();
    } catch (error) {
      console.error('Handle Google login success result failed:', error);
      setError(t('login.loginProcessingFailed') || '登录处理失败，请重试');
    }
  };

  // Google登录错误处理函数
  const handleGoogleLoginError = (error) => {
    console.error('Google login error:', error);
    setError(error.message || t('login.googleLoginFailed') || 'Google登录失败，请重试');
  };
  
  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post('/auth/login/send-code', {
        email
      });
      
      const data = response.data;
      
      if (data.success) {
        setResendTimer(60);
        
        const timer = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(data.message || t('login.sendCodeFailed') || 'Failed to resend verification code');
      }
    } catch (error) {
      console.error('Resend code error:', error);
      setError(t('login.sendCodeFailed') || 'Failed to resend verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = () => {
    setCurrentView('email');
  };

  const handleSendCode = async () => {
    if (!email) {
      setError(t('login.enterEmailError') || 'Please enter your email');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/auth/login/send-code', {
        email
      });
      
      const data = response.data;
      
      if (data.success) {
        setCurrentView('code');
        setResendTimer(60);
        
        const timer = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(data.message || t('login.sendCodeFailed') || 'Failed to send verification code');
      }
      
    } catch (error) {
      console.error('Send code error:', error);
      setError(t('login.sendCodeFailed') || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!verificationCode) {
      setError(t('login.enterCodeError') || 'Please enter verification code');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await loginWithCode(email, verificationCode);
      
      if (result.success) {
        onLoginSuccess && onLoginSuccess(result);
        onClose();
      } else {
        setError(result.error || t('login.loginFailed') || 'Login failed, please check verification code');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setError(t('login.loginFailed') || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentView === 'email') {
      setCurrentView('initial');
    } else if (currentView === 'code') {
      setCurrentView('email');
    }
    setError('');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <button 
            className="close-button" 
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: '1'
            }}
          >
            ×
          </button>
          <div className="logo">
            <img src="/logo.png" alt="Unlike raw API calls, our optimized Gemini interface provides a zero-config experience for creators." />
          </div>
          <h2>{t('login.signIn')}</h2>
          <p>{t('login.signInToAccount') || 'Sign in to your account'}</p>
          <div className="trial-info">
            <span>{t('login.freeTrialAvailable') || 'Free trial available after login'}</span>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {currentView === 'initial' && (
          <div className="modal-body">
            <GoogleLoginButton
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginError}
              text={t('login.signInWithGoogle')}
              className="google-btn"
              returnUrl={window.location.pathname + window.location.search}
            />
            <button className="email-btn" onClick={handleEmailContinue}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                <path fill="none" d="M0 0h24v24H0z"/>
                <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm17 4.238l-7.928 7.1L4 7.216V19h16V7.238zM4.511 5l7.55 6.662L19.502 5H4.511z" fill="rgba(255,255,255,1)"/>
              </svg>
              {t('login.continueWithEmail')}
            </button>
          </div>
        )}
        
        {currentView === 'email' && (
          <div className="modal-body">
            <p className="enter-email-text">{t('login.enterYourEmail')}</p>
            <div className="email-form">
              <label htmlFor="email">{t('login.email')}</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="m@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="send-code-btn" onClick={handleSendCode} disabled={isLoading}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" d="M0 0h24v24H0z"/>
                  <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm17 4.238l-7.928 7.1L4 7.216V19h16V7.238zM4.511 5l7.55 6.662L19.502 5H4.511z" fill="rgba(255,255,255,1)"/>
                </svg>
                {isLoading ? t('login.sending') : t('login.sendVerificationCode')}
              </button>
            </div>
            <button className="back-btn" onClick={handleBack}>{t('common.back')}</button>
          </div>
        )}
        
        {currentView === 'code' && (
          <div className="modal-body">
            <p className="enter-email-text">{t('login.enterYourEmail')}</p>
            <div className="email-form">
              <label htmlFor="email-display">{t('login.email')}</label>
              <input type="email" id="email-display" name="email-display" value={email} disabled />
              
              <div className="verification-code-container">
                <label htmlFor="verification-code">{t('login.verificationCode')}</label>
                <button 
                  type="button"
                  onClick={handleResendCode}
                  className="resend-link"
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0 ? `${t('login.resendIn')} ${resendTimer}s` : t('login.resend')}
                </button>
              </div>
              <input 
                type="text" 
                id="verification-code" 
                name="verification-code" 
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              
              <button className="signin-btn" onClick={handleSignIn} disabled={isLoading}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm-1 4h2v6h-2V8zm0 8h2v2h-2v-2z" fill="rgba(255,255,255,1)"/>
                </svg>
                {isLoading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </div>
            <button className="back-btn-code" onClick={handleBack}>{t('common.back')}</button>
          </div>
        )}
        
        <div className="modal-footer">
          <p>{t('login.byContining')} <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link-button">{t('login.privacyPolicy')}</a></p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
